import { isAtLeast21 } from './dateOfBirth';

const NOTIFICATION_CHANNELS = ['email', 'email_sms'];
const OPPORTUNITY_ALERTS = ['opted_in', 'opted_out'];

/**
 * Check the private minimum profile required before a user performs a critical action.
 * The returned diagnostics contain field names only, never private values.
 *
 * @param {Object} currentUser Sharetribe current user
 * @param {Date} today current date, injectable for tests
 * @returns {{isComplete: boolean, role: string|null, missingFields: string[], invalidFields: string[]}}
 */
export const getProfileCompletion = (currentUser, today = new Date()) => {
  const profile = currentUser?.attributes?.profile || {};
  const publicData = profile.publicData || {};
  const protectedData = profile.protectedData || {};
  const privateData = profile.privateData || {};
  const role = publicData.userType || null;
  const missingFields = [];
  const invalidFields = [];

  if (!protectedData.phoneNumber?.trim()) missingFields.push('phoneNumber');
  if (!privateData.dateOfBirth) missingFields.push('dateOfBirth');
  else if (!isAtLeast21(privateData.dateOfBirth, today)) invalidFields.push('dateOfBirth');

  if (!privateData.notificationChannel) missingFields.push('notificationChannel');
  else if (!NOTIFICATION_CHANNELS.includes(privateData.notificationChannel)) {
    invalidFields.push('notificationChannel');
  }

  if (role === 'provider') {
    if (!privateData.opportunityAlerts) missingFields.push('opportunityAlerts');
    else if (!OPPORTUNITY_ALERTS.includes(privateData.opportunityAlerts)) {
      invalidFields.push('opportunityAlerts');
    }
  }

  return {
    isComplete: missingFields.length === 0 && invalidFields.length === 0,
    role,
    missingFields,
    invalidFields,
  };
};

/**
 * Whether a provider is missing the public profile image required for provider actions.
 *
 * @param {Object} currentUser Sharetribe current user
 * @returns {boolean}
 */
export const isProviderMissingProfileImage = currentUser => {
  const role = currentUser?.attributes?.profile?.publicData?.userType;
  return role === 'provider' && !currentUser?.profileImage?.id;
};

const PROVIDER_COMMITMENT_TRANSITIONS = [
  'transition/accept',
  'transition/provider-accept-counter-offer',
];

export const requiresCompleteProviderProfile = transitionName =>
  PROVIDER_COMMITMENT_TRANSITIONS.includes(transitionName);
